import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const stored = localStorage.getItem("assetiq_user");
    const token = localStorage.getItem("assetiq_token");
    if (!token) {
      setLoading(false);
      return;
    }
    if (stored) setUser(JSON.parse(stored));
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("assetiq_user", JSON.stringify(data));
    } catch (e) {
      localStorage.removeItem("assetiq_token");
      localStorage.removeItem("assetiq_user");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("assetiq_token", data.token);
      localStorage.setItem("assetiq_user", JSON.stringify(data.user));
      setUser(data.user);
      // /auth/login returns a slim user without effective_capabilities. Fetch
      // /auth/me now so capability-gated UI (sidebar links, page guards)
      // renders correctly on the first render after login — otherwise a page
      // reload would be required.
      try {
        const me = await api.get("/auth/me");
        localStorage.setItem("assetiq_user", JSON.stringify(me.data));
        setUser(me.data);
        return { ok: true, user: me.data };
      } catch {
        return { ok: true, user: data.user };
      }
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || "Login failed" };
    }
  };

  const logout = async () => {
    // Best-effort backend revoke — clear local state either way
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("assetiq_token");
    localStorage.removeItem("assetiq_user");
    setUser(null);
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("assetiq_user", JSON.stringify(data));
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
